window.o_global = window.o_global || {};
window.o_util = window.o_util || {};
window.o_global.pali = window.o_global.pali || {};

/**
 * Sheet Object.
 */
window.o_global.pali.sheetBuilder = (function () {
  const defaultSettings = {
    url: null, // String: Load content from url.
    content: "", // String: Content to be displayed. Cannot be set in combination with url.
    title: "", // String: Title of the sheet.
    headerContent: "", // String: Header content to be displayed.
    menuContent: "", // String: Menu content to be displayed.
    openCallback: false, // String: Function callback when Sheet opens
    closeCallback: false, // String: Function callback when Sheet closes
    trackingObject: null, // String: Fire tracking event when opened.
    showBackButton: true, // Boolean: Used to disabled the backButton on content switch
    showCloseButton: true, // Boolean: Used to disabled the closeButton
    initialMobileHeight: null, // Number: Optional to give the sheet on mobile an initial height in pixel
    lockMobileHeight: true, // Boolean: Whether the sheet content should resize based on its contents (e.g. open/closed accordion panels) or if the initial height should be kept
    fullBleedContent: false, // Boolean: This can allow for content to take the full width of the sheet.
    contentGreyBackground: false, // Boolean: Determines whether the content area of the sheet should have a grey background instead of the default white.
    convertLayerLinks: false, // Boolean: Determines whether any - in the content - present links pointing to opening a layer should be converted to a sheet link.
  };

  const browserTransforms = ["webkitTransform", "msTransform", "transform"];

  const SHEET_EVENT = {
    OPENED: "assets.sheet.opened",
    ASYNC_CONTENT_LOADED: "assets.sheet.asyncContentLoaded",
    CLOSED: "assets.sheet.closed",
    CONTENT_SWITCHED: "assets.sheet.contentSwitched",
    CONTENT_REVERTED: "assets.sheet.contentReverted",
    ACTIVE_SHEET_CHANGED: "assets.sheet.activeSheetChanged",
    TRACKING: "tracking.bct.submitEvent",
  };

  const SHEET_CLOSE_TYPE = {
    CLOSED_WITH_X: "closedWithX",
    CLOSED_WITH_CURTAIN_CLICK: "closedWithCurtainClick",
    CLOSED_WITH_DRAG: "closedWithDrag",
    CLOSED_PROGRAMMATICALLY: "closedProgrammatically",
  };

  class Sheet {
    /**
     * Constructor.
     *
     * @param {Object} initialParameters
     */
    constructor(initialParameters) {
      let settings = {};
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const _self = this;

      const sheetCurtain = document.createElement("div");
      const sheetContainer = document.createElement("div");
      const sheetHeader = document.createElement("div");
      const sheetTitle = document.createElement("div");
      let sheetBackButton;
      let sheetCloseButton;
      let sheetContent = document.createElement("div");
      const sheetMenuBar = document.createElement("div");

      let isOpen = false;
      let currentSheetOffsetY = null;
      let initialTouchPos = null;
      let lastTouchPos = null;
      let rafPending = false;

      const contentStack = []; // Stack of saved content ids.
      const settingsStack = []; // Stack of saved settings.

      // Read Settings.
      settings = { ...defaultSettings, ...initialParameters };

      // Setting initial stack state
      settingsStack.push({ ...settings });

      // Set sheet classes.
      sheetCurtain.className = "pl_sheet-curtain";
      sheetContainer.className = "pl_sheet";
      sheetHeader.className = "pl_sheet__header";
      sheetTitle.className = "pl_headline100 pl_sheet__title";

      sheetContent.className = "pl_sheet__content";

      if (settings.fullBleedContent || settings.contentGreyBackground) {
        sheetContent.classList.add("pl_sheet__content--full-bleed");
      }

      if (settings.contentGreyBackground) {
        sheetContent.classList.add("pl_sheet__content--grey-bg");
      }

      sheetMenuBar.className = "pl_sheet__menu-bar";
      sheetContent.setAttribute("id", `pl_sheet__content__${contentStack.length}`);

      // Add utility classes
      sheetContainer.classList.add("pl_sheet--hidden");

      /**
       * Get the element of the sheet container
       * @returns The sheet container element
       */
      this.getContainer = function () {
        return sheetContainer;
      };

      /**
       * Get the element of the sheet back button
       * @returns The sheet back button element
       */
      this.getBackButton = function () {
        return sheetBackButton;
      };

      /**
       * Get the element of the sheet close button
       * @returns The sheet close button element
       */
      this.getCloseButton = function () {
        return sheetCloseButton;
      };

      /**
       * Get the element of the sheet header
       * @returns The sheet header element
       */
      this.getHeader = function () {
        return sheetHeader;
      };

      /**
       * Get the element of the sheet title
       * @returns The sheet title element
       */
      this.getTitle = function () {
        return sheetTitle;
      };

      /**
       * Get the element of the sheet content
       * @returns The sheet content element
       */
      this.getContent = function () {
        return sheetContent;
      };

      /**
       * Get the element of the sheet menuBar
       * @returns The sheet menuBar element
       */
      this.getMenuBar = function () {
        return sheetMenuBar;
      };

      /**
       * Get the instance of the sheet's settings
       * @returns The sheet settings object
       */
      this.getSettings = function () {
        return settings;
      };

      /**
       * Manually execute preload scripts inside of the sheet.
       *
       * @param {Object} element      Inside of this element preload script should be executed.
       */
      this.executePreloadScripts = function (element) {
        if (window.preload_polyfill_invoke) {
          window.preload_polyfill_invoke(element);
        }
      };

      /**
       * Read data attribute set in Shopoffice resources and set sheet title.
       */
      this.extractTitleFromContent = function () {
        const prerenderedContent = sheetContent.querySelector("[data-title]");

        if (prerenderedContent) {
          const parsedTitle = prerenderedContent.getAttribute("data-title");

          _self.setTitle(parsedTitle);

          const duplicatedTitle = prerenderedContent.querySelector(
            ".js_layerTitle, .js_sheetTitle"
          );

          if (duplicatedTitle) {
            duplicatedTitle.parentNode.removeChild(duplicatedTitle);
          }
        }
      };

      /**
       * Convert any present layer links in content to sheet pendants.
       */
      this.convertPresentLayerLinksToSheetLinks = function () {
        const foundPaliLayerLinks = sheetContent.querySelectorAll(".js_openInPaliLayer");

        foundPaliLayerLinks.forEach((layerLink) => {
          layerLink.classList.remove("js_openInPaliLayer");
          const sheetLink = document.createElement("a");

          sheetLink.innerText = layerLink.innerText;

          const newClassList = ["js_openInPaliSheet", ...layerLink.classList];

          sheetLink.classList.add(...newClassList);

          sheetLink.setAttribute("data-sheet-url", layerLink.href);

          layerLink.parentNode.replaceChild(sheetLink, layerLink);
        });
      };

      /**
       * Set content of element.
       *
       * @private
       *
       * @param {String|Object|Array} content       Content to be inserted.
       * @param {Object} element              Element the content should be inserted into (either pl_sheet__content).
       */
      this.setElementContent = function (content, element) {
        if (typeof content === "string") {
          // eslint-disable-next-line no-param-reassign
          element.innerHTML = content;
          element.classList.remove("pl_sheet__content--loading");
        } else {
          _self.clearChildren(element);

          if (Array.isArray(content)) {
            content.forEach((e) => element.appendChild(e));
          } else {
            element.appendChild(content);
          }
        }

        // Execute script tags.
        window.o_util.hardcore.executeInlineScripts(element);

        // TODO: This cannot be before executeInlineScripts because then the scripts will be executed twice ....
        _self.executePreloadScripts(element);
      };

      /**
       * Handle the ajax result.
       *
       * @param {Object} result       Object with the ajax result.
       * @param {Function} [callback]     Callback to execute after content is loaded.
       */
      this.handleAjaxResult = function (result, callback) {
        const defaultCallbacks = {
          200(response) {
            _self.setContent(response.responseText);

            if (typeof callback === "function") {
              callback(response.responseText, response.status);
            }

            window.o_global.eventQBus.emit(SHEET_EVENT.ASYNC_CONTENT_LOADED);
          },
          default() {
            const errorTemplate =
              '<div class="p_message p_message--error pl_sheet--error">' +
              "<b>Entschuldigung!</b> Aufgrund von technischen Problemen kann der Inhalt leider nicht " +
              "geladen werden. Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut." +
              "</div>";

            _self.setContent(errorTemplate);
            if (typeof callback === "function") {
              callback(errorTemplate, result.status);
            }
          },
        };
        // Merging default ajax callbacks with given initialParameters in
        const statusCallbacks = window.o_util.core.extend(
          defaultCallbacks,
          settings.statusCallbacks,
          true,
          false
        );

        if (statusCallbacks[result.status]) {
          statusCallbacks[result.status](result);
        } else {
          statusCallbacks.default();
        }
      };

      /**
       * Remove modalCurtain from dom.
       *
       * @private
       */
      this.removeSheetAndCurtain = function () {
        // Delete elements from DOM
        document.getElementsByTagName("body")[0].removeChild(sheetContainer);
        document.getElementsByTagName("body")[0].removeChild(sheetCurtain);
      };

      /**
       * Set back button.
       *
       * @param {Function} callback       Function to be executed on click.
       * @param {String|Object} title     Back button to be inserted.
       */
      this.setBackButton = function (callback, title) {
        _self.removeBackButton();

        _self.buildBackButtonElement(title);

        sheetBackButton.addEventListener("click", callback);
        sheetHeader.insertBefore(sheetBackButton, sheetTitle);
      };

      this.buildBackButtonElement = function (title) {
        sheetBackButton = document.createElement("button");
        sheetBackButton.className = "pl_sheet__back-button";

        sheetBackButton.appendChild(document.createElement("i"));

        if (title) {
          const titleElement = document.createElement("span");
          titleElement.setAttribute("aria-label", title);
          titleElement.innerHTML = title;
          sheetBackButton.appendChild(titleElement);
        } else {
          sheetBackButton.setAttribute("aria-label", "Zurück");
        }
      };

      this.removeBackButton = function () {
        if (sheetHeader.contains(sheetBackButton)) {
          sheetHeader.removeChild(sheetBackButton);
          sheetBackButton = null;
        }
      };

      this.setCloseButton = function () {
        _self.removeCloseButton();

        sheetCloseButton = document.createElement("button");
        sheetCloseButton.className = "pl_sheet__close-button";
        sheetCloseButton.setAttribute("aria-label", "Schließen");
        sheetCloseButton.appendChild(document.createElement("i"));

        sheetHeader.appendChild(sheetCloseButton);

        sheetCloseButton.addEventListener("click", () => {
          _self.close(SHEET_CLOSE_TYPE.CLOSED_WITH_X);
        });
      };

      this.removeCloseButton = function () {
        if (sheetHeader.contains(sheetCloseButton)) {
          sheetHeader.removeChild(sheetCloseButton);
          sheetCloseButton = null;
        }
      };

      /**
       * Set content of pl_sheet__header div.
       *
       * @param {String|HTMLElement|Array} header       Header to be inserted.
       * @param {Boolean} updateContentHeight
       */
      this.setHeader = function (header, updateContentHeight = true) {
        if (!header) {
          return;
        }

        _self.setElementContent(header, sheetHeader);

        if (updateContentHeight) {
          _self.updateContentMaxHeight();
        }
      };

      /**
       * Set content of pl_sheet__title div.
       *
       * @param {String|HTMLElement|Array} title       Title to be inserted.
       * @param {Boolean} updateContentHeight       Whether the sheet content max-height should be recalculcated.
       */
      this.setTitle = function (title, updateContentHeight = true) {
        if (!title) {
          return;
        }

        sheetTitle.innerText = "";
        _self.setElementContent(title, sheetTitle);

        if (updateContentHeight) {
          _self.updateContentMaxHeight();
        }
      };

      /**
       * Set content of pl_sheet__content div.
       *
       * @param {String|HTMLElement|Array} content       Content to be inserted.
       */
      this.setContent = function (content) {
        if (!content) {
          return;
        }

        _self.setElementContent(content, sheetContent);

        _self.extractTitleFromContent();

        if (settings.convertLayerLinks) {
          _self.convertPresentLayerLinksToSheetLinks();
        }
      };

      /**
       * Set content of pl_sheet__menu-bar div.
       *
       * @param {String|HTMLElement|Array} menuBarContent       Content to be inserted.
       * @param {Boolean} updateContentHeight
       */
      this.setMenuBar = function (menuBarContent, updateContentHeight = true) {
        if (!menuBarContent) {
          return;
        }

        _self.setElementContent(menuBarContent, sheetMenuBar);

        sheetContainer.appendChild(sheetMenuBar);

        sheetContainer.classList.remove("pl_sheet__menu-bar-hidden");

        if (updateContentHeight) {
          _self.updateContentMaxHeight();
        }
      };

      /**
       * Hide the MenuBar.
       *
       * @param {Boolean} updateContentHeight
       */
      this.hideMenuBar = function (updateContentHeight = true) {
        sheetContainer.classList.add("pl_sheet__menu-bar-hidden");

        if (updateContentHeight) {
          _self.updateContentMaxHeight();
        }
      };

      this.updateContentMaxHeight = function () {
        if (window.matchMedia("only screen and (max-width: 479px)").matches) {
          sheetContent.style.maxHeight = `${
            sheetContainer.clientHeight - sheetHeader.clientHeight - sheetMenuBar.clientHeight > 0
              ? sheetContainer.clientHeight - sheetHeader.clientHeight - sheetMenuBar.clientHeight
              : 24
          }px`;
        } else {
          sheetContent.style.maxHeight = `calc(100vh - ${sheetHeader.clientHeight}px - ${sheetMenuBar.clientHeight}px)`;
        }
      };

      /**
       * Sets Content via AJAX.
       *
       * @param {String} url              Url to load.
       * @param {Function} [callback]     Callback to execute after content is loaded.
       */
      this.setContentFromURL = function (url, callback) {
        window.o_util.ajax.get(url, function (ajaxResult) {
          _self.handleAjaxResult(ajaxResult, callback);
        });
      };

      this.setSheetHeightSpecifics = function () {
        if (
          (settings.initialMobileHeight || settings.lockMobileHeight) &&
          window.matchMedia("only screen and (max-width: 479px)").matches
        ) {
          if (settings.url) {
            window.o_global.eventQBus.on(SHEET_EVENT.ASYNC_CONTENT_LOADED, () => {
              _self.updateContainerHeight();
              _self.updateContentMaxHeight();
            });
          } else {
            _self.updateContainerHeight();
            _self.updateContentMaxHeight();
          }
        } else if (window.matchMedia("only screen and (min-width: 480px)").matches) {
          _self.updateContentMaxHeight();
        }
      };

      this.updateContainerHeight = function () {
        sheetContainer.style.height = settings.initialMobileHeight
          ? settings.initialMobileHeight
          : `${sheetContainer.clientHeight}px`;
      };

      /**
       * Open Sheet.
       */
      this.open = function () {
        if (!isOpen) {
          if (settings.headerContent) {
            _self.setHeader(settings.headerContent, false);
          } else {
            _self.setTitle(settings.title ? settings.title : "", false);

            sheetHeader.appendChild(sheetTitle);

            if (settings.showCloseButton) {
              _self.setCloseButton();
            }
          }

          // Load Content.
          if (settings.content) {
            _self.setContent(settings.content);
          } else if (settings.url) {
            _self.setContentFromURL(settings.url);
          } else {
            _self.setContent("");
          }

          if (settings.menuContent) {
            _self.setMenuBar(settings.menuContent, false);
          } else {
            // sheetContainer.classList.add("pl_sheet__menu-bar-hidden");
            _self.hideMenuBar(false);
          }

          // Prepare DOM elements
          sheetCurtain.classList.add("pl_sheet-curtain--hidden");
          sheetCurtain.addEventListener("click", () => {
            _self.close(SHEET_CLOSE_TYPE.CLOSED_WITH_CURTAIN_CLICK);
          });

          sheetContainer.appendChild(sheetHeader);
          sheetContainer.appendChild(sheetContent);
          sheetContainer.appendChild(sheetMenuBar);

          // Add elements to DOM.
          document.getElementsByTagName("body")[0].appendChild(sheetCurtain);
          document.getElementsByTagName("body")[0].appendChild(sheetContainer);
          isOpen = true;

          // Check and set in case the height of sheet should be treated specifically (e.g. initial or locked height)
          _self.setSheetHeightSpecifics();

          // Emit event to register this instance centrally.
          window.o_global.eventQBus.emit(SHEET_EVENT.ACTIVE_SHEET_CHANGED, _self);

          // One call to opacity attribute is necessary to make the animation work.
          // eslint-disable-next-line no-unused-expressions
          window.getComputedStyle(sheetContainer).opacity;

          sheetContainer.classList.remove("pl_sheet--hidden");
          sheetContainer.classList.add("pl_sheet--open");
          sheetCurtain.classList.remove("pl_sheet-curtain--hidden");

          if (window.o_global.breakpoint.isBreakpointActive(["s"])) {
            _self.registerGestureEvents();
          }

          // Removing focus from trigger, to avoid firing "click" events when user presses "Enter" or the spacebar
          if (document.activeElement) {
            document.activeElement.blur();
          }

          // Await 250ms opening transition before firing the remaining events
          setTimeout(() => {
            // Execute the sheet opened callback
            if (settings.openCallback) {
              settings.openCallback();
            }

            // Send Tracking Event to Server
            if (settings.trackingObject) {
              window.o_global.eventQBus.emit(
                SHEET_EVENT.TRACKING,
                JSON.parse(settings.trackingObject)
              );
            }

            // fire open event
            window.o_global.eventQBus.emit(SHEET_EVENT.OPENED, _self.exposeInterface());
          }, 250);
        }
      };

      /**
       * Register event handlers for gesture handling
       *
       * @private
       *
       */
      this.registerGestureEvents = function () {
        // Check if pointer events are supported.
        if (window.PointerEvent) {
          // Add pointer event listeners
          sheetHeader.addEventListener("pointerdown", _self.handleGestureStart, true);
          sheetHeader.addEventListener("pointerup", _self.handleGestureEnd, true);
          sheetHeader.addEventListener("pointercancel", _self.handleGestureEnd, true);
        } else {
          // add fallback listeners if no pointer support present
          // Add touch listener
          sheetHeader.addEventListener("touchstart", _self.handleGestureStart, true);
          sheetHeader.addEventListener("touchend", _self.handleGestureEnd, true);
          sheetHeader.addEventListener("touchcancel", _self.handleGestureEnd, true);

          // Add mouse listener
          sheetHeader.addEventListener("mousedown", _self.handleGestureStart, true);
        }
      };

      /**
       * Close Sheet.
       *
       * @param {String} closeType
       * String describing how the sheet was closed.
       * If none was passed, we assume a programmatic close.
       */
      this.close = function (closeType = SHEET_CLOSE_TYPE.CLOSED_PROGRAMMATICALLY) {
        if (isOpen) {
          // Emit event to de-register this instance centrally.
          window.o_global.eventQBus.emit(SHEET_EVENT.ACTIVE_SHEET_CHANGED);

          isOpen = false;

          sheetContainer.classList.remove("pl_sheet--open");
          sheetContainer.classList.add("pl_sheet--close");
          sheetCurtain.classList.add("pl_sheet-curtain--hidden");

          setTimeout(function () {
            sheetContainer.classList.add("pl_sheet--hidden");
            sheetContainer.classList.remove("pl_sheet--close");

            if (settings.closeCallback) {
              settings.closeCallback({ closeType });
            }

            _self.removeSheetAndCurtain();

            // fire close event
            window.o_global.eventQBus.emit(SHEET_EVENT.CLOSED, { closeType });
          }, 250);
        }
      };

      /**
       * Handler of the gesture start. Registers move events listeners and updates starting coordinates.
       *
       * @private
       *
       * @param {PointerDown, TouchStart, MouseDown} event
       */
      this.handleGestureStart = function (event) {
        event.preventDefault();

        if (event.touches && event.touches.length > 1) {
          return;
        }

        // If supported register the move and end listeners for pointer
        if (window.PointerEvent) {
          sheetHeader.addEventListener("pointermove", _self.handleGestureMove, true);
          event.target.setPointerCapture(event.pointerId);
        } else {
          // Register mouse and touch listeners if no pointer support present in browser
          document.addEventListener("touchmove", _self.handleGestureMove, true);
          document.addEventListener("mousemove", _self.handleGestureMove, true);
          document.addEventListener("mouseup", _self.handleGestureEnd, true);
        }

        initialTouchPos = _self.getGesturePointFromEvent(event);

        sheetContainer.style.transition = "initial";
      };

      /**
       * Handling of the actual gesture
       *
       * @private
       *
       * @param {PointerMove,TouchMove,MouseMove} event
       * @returns
       */
      this.handleGestureMove = function (event) {
        event.preventDefault();

        if (!initialTouchPos) {
          return;
        }

        lastTouchPos = _self.getGesturePointFromEvent(event);

        if (rafPending) {
          return;
        }

        rafPending = true;

        window.requestAnimationFrame(_self.executeVerticalDrag);
      };

      /**
       * Deregistering of listeners and handling of gesture end
       * @param {*} event Gesture End Event
       */
      this.handleGestureEnd = function (event) {
        event.preventDefault();

        if (event.touches && event.touches.length > 0) {
          return;
        }

        rafPending = false;

        // Remove Event Listeners
        if (window.PointerEvent) {
          event.target.releasePointerCapture(event.pointerId);
          sheetHeader.removeEventListener("pointermove", _self.handleGestureMove, true);
        } else {
          // Remove Mouse Listeners
          document.removeEventListener("touchmove", _self.handleGestureMove, true);
          document.removeEventListener("mousemove", _self.handleGestureMove, true);
          document.removeEventListener("mouseup", _self.handleGestureEnd, true);
        }

        initialTouchPos = null;

        if (currentSheetOffsetY / sheetContainer.clientHeight >= 0.4) {
          // if dragged more than half its height, close sheet
          window.requestAnimationFrame(_self.initiateClosingAnmiation);
          _self.close(SHEET_CLOSE_TYPE.CLOSED_WITH_DRAG);
        } else {
          // else snap back to initial position
          window.requestAnimationFrame(_self.returnToInitialPos);
        }
      };

      /**
       * Function that vertically moves the target element according to drag gesture
       */
      this.executeVerticalDrag = function () {
        if (!rafPending) {
          return;
        }

        const differenceInY = lastTouchPos.y - initialTouchPos.y;

        if (differenceInY > 0) {
          currentSheetOffsetY = differenceInY;

          const transformStyle = `translateY(${differenceInY}px)`;

          sheetContainer.style.MozTransform = transformStyle;
          sheetContainer.style.msTransform = transformStyle;
          sheetContainer.style.transform = transformStyle;
        }

        rafPending = false;
      };

      /**
       * Returns the sheet to its orginial starting position
       */
      this.returnToInitialPos = function () {
        // reseting style set via js
        sheetContainer.style.transition = "";
        _self.setSheetContainerTransformStyleCrossBrowser("");

        currentSheetOffsetY = 0;

        rafPending = false;
      };

      /**
       * Initiates the closing animation after drag gesture
       */
      this.initiateClosingAnmiation = function () {
        sheetContainer.style.transition = "transform 300ms ease-in";
        const transformStyle = `translateY(${sheetContainer.clientHeight}px)`;
        _self.setSheetContainerTransformStyleCrossBrowser(transformStyle);

        rafPending = false;
      };

      /**
       * Helper function for coordinates of touch event
       * @param {*} event
       * @returns X,Y coordinates of touch event as Point.
       */
      this.getGesturePointFromEvent = function (event) {
        const point = {};

        if (event.targetTouches) {
          // Prefer Touch Events
          point.x = event.targetTouches[0].clientX;
          point.y = event.targetTouches[0].clientY;
        } else {
          // Either Mouse event or Pointer Event
          point.x = event.clientX;
          point.y = event.clientY;
        }

        return point;
      };

      /**
       * Sets the transform style of the sheet container for all supported browsers
       * @param {string} transformStyle
       */
      this.setSheetContainerTransformStyleCrossBrowser = function (transformStyle) {
        browserTransforms.forEach((key) => {
          sheetContainer.style[key] = transformStyle;
        });
      };

      /**
       * Pushes existing content onto stack and inserts new content.
       *
       * @param {Object} initParameters    Sheet settings.
       */
      this.switchSheetContent = function (initParameters) {
        // save old settings for later use.
        settingsStack.push({ ...settings });

        // apply default settings to new settings
        settings = { ...defaultSettings, ...initParameters };

        // add back button if not there yet and not disabled
        if (settings.showBackButton && !sheetHeader.contains(sheetBackButton)) {
          _self.setBackButton(_self.revertSheetContent);
        }

        // hide old content and add it to stack
        sheetContent.classList.add("hide");
        contentStack.push(`pl_sheet__content__${contentStack.length}`);

        // create new content container for the switched content
        _self.createEmptySwitchContentContainer();

        // fill new container
        if (settings.content) {
          _self.setContent(settings.content);
        } else if (settings.url) {
          _self.setContentFromURL(settings.url);
        }

        if (settings.title) {
          _self.setTitle(settings.title, false);
        }

        _self.clearChildren(sheetMenuBar);

        // set new menuBar content
        if (settings.menuContent) {
          _self.setMenuBar(settings.menuContent, false);
        } else {
          _self.hideMenuBar(false);
        }

        if (settings.url) {
          window.o_global.eventQBus.on(SHEET_EVENT.ASYNC_CONTENT_LOADED, () => {
            _self.updateContentMaxHeight();
          });
        } else {
          _self.updateContentMaxHeight();
        }

        // execute open callback
        if (settings.openCallback) {
          settings.openCallback();
        }

        // Send Tracking Event to Server
        if (settings.trackingObject) {
          window.o_global.eventQBus.emit(SHEET_EVENT.TRACKING, JSON.parse(settings.trackingObject));
        }

        // emit content switched event
        window.o_global.eventQBus.emit(SHEET_EVENT.CONTENT_SWITCHED, sheetContent);
      };

      /**
       * Creates an empty sheet content element for switching between multiple contents.
       *
       * @private
       */
      this.createEmptySwitchContentContainer = function () {
        const newContentContainer = document.createElement("div");

        newContentContainer.classList.add("pl_sheet__content");

        // setting id with current stack depth for content switch
        newContentContainer.setAttribute("id", `pl_sheet__content__${contentStack.length}`);

        // setting new container as contentElement instance
        sheetContent = newContentContainer;

        // insert new container into the DOM
        sheetContainer.insertBefore(sheetContent, sheetMenuBar);
      };

      /**
       * Pops content from stack and reinserts old content.
       *
       * @private
       */
      this.revertSheetContent = function () {
        if (contentStack.length !== 0) {
          if (sheetContent) {
            sheetContainer.removeChild(sheetContent);
          }

          sheetContent = document.getElementById(contentStack.pop());

          sheetContent.classList.toggle("hide");

          // revert to previous settings
          const { closeCallback } = settings;
          settings = settingsStack.pop();

          if (!settings.showBackButton || contentStack.length === 0) {
            _self.removeBackButton();
          }

          if (settings.title) {
            _self.setTitle(settings.title, false);
          }

          _self.clearChildren(sheetMenuBar);

          // set new menuBar content
          if (settings.menuContent) {
            _self.setMenuBar(settings.menuContent, false);
          } else {
            _self.hideMenuBar(false);
          }

          _self.updateContentMaxHeight();

          if (closeCallback) {
            closeCallback();
          }

          window.o_global.eventQBus.emit(SHEET_EVENT.CONTENT_REVERTED);
        }
      };

      /**
       * Removes all children of the given element
       */
      this.clearChildren = function (element) {
        if (element.children.length > 0) {
          [].forEach.call(Array.from(element.children), (childNode) => {
            element.removeChild(childNode);
          });
        }
      };

      /**
       * Returns the public api for the sheet
       * @returns sheet api object
       */
      this.exposeInterface = function () {
        return {
          close: () => _self.close(),
          open: () => _self.open(),
          getSettings: () => _self.getSettings(),
          back: () => _self.revertSheetContent(),
          getContainer: () => _self.getContainer(),
          setBackButton: (callback, title) => _self.setBackButton(callback, title),
          getBackButton: () => _self.getBackButton(),
          removeBackButton: () => _self.removeBackButton(),
          setCloseButton: () => _self.setCloseButton(),
          getCloseButton: () => _self.getCloseButton(),
          removeCloseButton: () => _self.removeCloseButton(),
          setHeader: (header) => _self.setHeader(header),
          getHeader: () => _self.getHeader(),
          setTitle: (title) => _self.setTitle(title),
          getTitle: () => _self.getTitle(),
          setContent: (content) => _self.setContent(content),
          getContent: () => _self.getContent(),
          setMenuBar: (menuBar) => _self.setMenuBar(menuBar),
          getMenuBar: () => _self.getMenuBar(),
          hideMenuBar: () => _self.hideMenuBar(),
        };
      };
    }
  }
  return Sheet;
})();
